import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { EmspUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/emsp-users/emsp-user.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import {
  useEmspBoGetEmspUserQuery,
  useLazyEmspBoGetEmspUserLockStatusQuery,
} from '@app/core/service/emsp-bo-emsp-user.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

import { EmspBoEmspUserLockCreateController } from '../emsp-bo-emsp-user-lock-create/emsp-bo-emsp-user-lock.controller';
import { EmspBoEmspUserLockDeleteController } from '../emsp-bo-emsp-user-lock-delete/emsp-bo-emsp-user-delete.controller';
import {
  EmspBoEmspUserContextProvider,
  EmspBoEmspUserLockStatusContextProvider,
} from '../emsp-bo-emsp-user.context';
import { EmspBoEmspUserDetailsView } from './emsp-bo-emsp-user-details.view';

interface EmspBoEmspUserDetailsControllerProps {
  resource: EmspUserModel;
}

const EmspBoEmspUserDetailsController = (
  props: EmspBoEmspUserDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canLock = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.LOCK_EMSP_USER,
      }),
    [userPermissions],
  );

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionLockCreateIsDialogOpen, setActionLockCreateIsDialogOpen] =
    useState(false);

  const [actionLockDeleteIsDialogOpen, setActionLockDeleteIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const [lazyGetEmspUserLockStatusQuery, { data: emspUserLockStatus = null }] =
    useLazyEmspBoGetEmspUserLockStatusQuery();

  useEffect(() => {
    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.READ_EMSP_USER_LOCK_STATUS,
      })
    ) {
      lazyGetEmspUserLockStatusQuery({ userId: resource.id });
    }
  }, [lazyGetEmspUserLockStatusQuery, resource.id, userPermissions]);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(`${resource.firstName} ${resource.lastName}`);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      canLock &&
      emspUserLockStatus !== null &&
      !emspUserLockStatus.isLocked &&
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.LOCK_EMSP_USER,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="lock-create"
          icon="lock-unlocked-01"
          onClick={() => setActionLockCreateIsDialogOpen(true)}
        />,
      );
    }

    if (
      canLock &&
      emspUserLockStatus?.isLocked &&
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.LOCK_EMSP_USER,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="lock-create"
          icon="lock-01"
          onClick={() => setActionLockDeleteIsDialogOpen(true)}
        />,
      );
    }

    setPageAction(actions);
  }, [canLock, emspUserLockStatus, setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <EmspBoEmspUserContextProvider data={resource}>
      <EmspBoEmspUserLockStatusContextProvider data={emspUserLockStatus}>
        <EmspBoEmspUserDetailsView />
        <Dialog
          open={actionLockCreateIsDialogOpen}
          onClose={() => setActionLockCreateIsDialogOpen(false)}>
          <Dialog.Header
            title={t('$.emsp-bo-emsp-user-lock-create.title')}
            icon="lock-01"
            onClose={() => setActionLockCreateIsDialogOpen(false)}
          />
          <Dialog.Content>
            <EmspBoEmspUserLockCreateController
              onCancel={() => setActionLockCreateIsDialogOpen(false)}
              onSuccess={() => setActionLockCreateIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
        <Dialog
          open={actionLockDeleteIsDialogOpen}
          onClose={() => setActionLockDeleteIsDialogOpen(false)}>
          <Dialog.Header
            title={t('$.emsp-bo-emsp-user-lock-delete.title')}
            icon="lock-01"
            onClose={() => setActionLockDeleteIsDialogOpen(false)}
          />
          <Dialog.Content>
            <EmspBoEmspUserLockDeleteController
              onCancel={() => setActionLockDeleteIsDialogOpen(false)}
              onSuccess={() => setActionLockDeleteIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
      </EmspBoEmspUserLockStatusContextProvider>
    </EmspBoEmspUserContextProvider>
  );
};

export const EmspBoEmspUserDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useEmspBoGetEmspUserQuery({ userId: params.id ?? '' });

  const { Component } = withProtectedResource<EmspUserModel, {}, EmspUserModel>(
    EmspBoEmspUserDetailsController,
    queryState,
    {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.EMSP_BO,
          action: ActionsByResource.EMSP_BO.READ_EMSP_USER,
        }),
    },
  )({});

  return Component;
};
